import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Building2, Calendar, Loader2 } from "lucide-react";
import RevenueSnapshot from "@/components/noxn/RevenueSnapshot";
import FunnelSnapshot from "@/components/noxn/FunnelSnapshot";
import ReferralLeaderboard from "@/components/noxn/ReferralLeaderboard";
import ProfitabilityReport from "@/components/noxn/ProfitabilityReport";
import { useAuth, api } from "@/lib/auth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PortalShell } from "@/components/account/PortalShell";

const STATUS_ACCENT = {
    new: "border-white/20 text-white",
    approved: "border-sky-400/50 text-sky-300",
    live: "border-emerald-400/60 text-emerald-300",
    done: "border-slate-500/60 text-slate-300",
    cancelled: "border-[#FF5A00]/60 text-[#FF5A00]",
};

const MyAssignedCampaigns = ({ userId }) => {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get("/campaigns").then(({ data }) => setRows((data || []).filter((c) => c.owner_id === userId).slice(0, 6)))
            .catch(() => setRows([]));
    }, [userId]);
    return (
        <div className="mt-6 border border-white/10 bg-[#1B2E55]/50 p-6" data-testid="dashboard-my-campaigns">
            <div className="flex items-center justify-between mb-2">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ My assigned campaigns</div>
                <Link to="/dashboard/pipeline" data-testid="dashboard-my-campaigns-open-pipeline"
                    className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-1">
                    Open pipeline <ArrowUpRight size={11} />
                </Link>
            </div>
            {rows === null ? (
                <div className="flex items-center gap-2 text-slate-500 text-xs py-3"><Loader2 size={13} className="animate-spin" /> Loading…</div>
            ) : rows.length === 0 ? (
                <div className="text-xs text-slate-500 font-mono-tech uppercase tracking-widest py-3" data-testid="dashboard-my-campaigns-empty">
                    Nothing claimed yet — check the New Campaign Submissions inbox in Pipeline.
                </div>
            ) : (
                <div className="space-y-2" data-testid="dashboard-my-campaigns-list">
                    {rows.map((c) => (
                        <Link key={c.id} to={`/dashboard/pipeline?campaign=${c.id}`} data-testid={`dashboard-my-campaign-${c.id}`}
                            className="flex flex-wrap items-center gap-3 border border-white/10 hover:border-[#FF5A00]/50 px-3 py-2.5 transition-colors">
                            <Building2 size={13} className="text-[#FF5A00] shrink-0" />
                            <div className="flex-1 min-w-[160px] text-white text-sm truncate">{c.business_name || "(no name)"}</div>
                            <div className="text-[10px] font-mono-tech text-slate-500 flex items-center gap-1"><Calendar size={10} /> {c.launch_date || "—"}</div>
                            <span className={`px-1.5 py-0.5 text-[9px] font-mono-tech uppercase tracking-widest border ${STATUS_ACCENT[c.pipeline_status] || "border-white/15 text-slate-400"}`}>
                                {c.pipeline_status}
                            </span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

export default function Dashboard() {
    usePageMeta({ title: "Dashboard · NOXN", description: "Your NOXN campaigns and invoices." });
    const { user } = useAuth();

    return (
        <PortalShell title="Dashboard" mainClassName="p-0">
        <div className="min-h-screen w-full relative">
            <div className="absolute inset-0 -z-10 grid-noise opacity-25" />

            <main className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
                <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, ease:[0.16,1,0.3,1] }}>
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#FF5A00] mb-4 flex items-center gap-3">
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" /> Dashboard
                    </div>
                    <h1 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                        Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
                    </h1>
                    <p className="mt-6 max-w-xl text-slate-400 text-base">
                        Your campaigns, invoices and booking approvals will land here. Phase 1 is auth only, more is on the way.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 0.1, ease:[0.16,1,0.3,1] }}
                    className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
                >
                    {[
                        { k: "My campaigns", v: "0 active", cta: "Coming soon" },
                        { k: "Invoices", v: "0 outstanding", cta: "Coming soon" },
                        { k: "Support", v: "Reply directly", cta: "hello@noxn.com.au" },
                    ].map((c, i) => (
                        <div key={i} className="relative p-8 bg-[#1B2E55] border border-white/10 card-lift noxn-shimmer">
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">/ {c.k}</div>
                            <div className="font-display font-black tracking-tighter text-3xl text-white">{c.v}</div>
                            <div className="mt-4 text-xs text-slate-400 flex items-center gap-2">{c.cta} <ArrowUpRight size={12} /></div>
                        </div>
                    ))}
                </motion.div>

                {user?.role !== "client" && <RevenueSnapshot />}
                {user?.role !== "client" && <ProfitabilityReport />}
                {user?.role !== "client" && <FunnelSnapshot />}
                {user?.role !== "client" && <ReferralLeaderboard />}

                {/* Client portal card — visible to everyone; primary for clients */}
                <div className="mt-16 border border-white/10 bg-[#1B2E55]/50 p-6" data-testid="dashboard-portal-card">
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] mb-2">/ My campaigns</div>
                    <div className="text-white text-sm">
                        Track every campaign you&apos;ve booked with NOXN — status, screens on air, and contract state, all in one place.
                    </div>
                    <div className="mt-5">
                        <Link to="/portal" data-testid="dashboard-portal-link"
                            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors">
                            Open my portal <ArrowUpRight size={12} />
                        </Link>
                    </div>
                </div>

                {user?.role !== "client" && (
                    <div className="mt-6 border border-[#FF5A00]/40 bg-[#1B2E55]/70 p-6">
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] mb-2">/ Staff area</div>
                        <div className="text-white text-sm">
                            You&apos;re signed in as <span className="font-mono-tech uppercase tracking-widest text-[#FF5A00]">{user.role}</span>.
                            Manage inventory now — pipeline &amp; contracts arrive in the next phase.
                        </div>
                        <div className="mt-5 flex flex-wrap gap-3">
                            <Link to="/dashboard/screens" data-testid="dashboard-screens-link"
                                className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors">
                                Screen inventory <ArrowUpRight size={12} />
                            </Link>
                            <Link to="/dashboard/pipeline" data-testid="dashboard-pipeline-link"
                                className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors">
                                Pipeline <ArrowUpRight size={12} />
                            </Link>
                            <Link to="/dashboard/tasks" data-testid="dashboard-tasks-link"
                                className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors">
                                Operations board <ArrowUpRight size={12} />
                            </Link>
                            <Link to="/dashboard/comms" data-testid="dashboard-comms-link"
                                className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors">
                                Comms <ArrowUpRight size={12} />
                            </Link>
                            <Link to="/dashboard/crm" data-testid="dashboard-crm-link"
                                className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors">
                                CRM <ArrowUpRight size={12} />
                            </Link>
                            <Link to="/dashboard/staff" data-testid="dashboard-staff-link"
                                className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors">
                                Staff management <ArrowUpRight size={12} />
                            </Link>
                            <Link to="/dashboard/admin" data-testid="dashboard-admin-link"
                                className="inline-flex items-center gap-2 border border-[#FF5A00]/40 hover:border-[#FF5A00] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors">
                                Admin console <ArrowUpRight size={12} />
                            </Link>
                        </div>
                        <MyAssignedCampaigns userId={user.id} />
                    </div>
                )}
            </main>
        </div>
        </PortalShell>
    );
}
